import { createSelector } from 'reselect';
import { AnalogInput, AnalogOutput, AnalogTemperatureSensor, BoilerAdapter, GuardZone, HeatingCircuit, HeatingMode, Pump, RadioSensor, RadioSensor433, RelayControl, ThreewayTap, WebElement, Z3KObjectId, } from 'z3k-config';
import { getDeviceSpec } from 'z3k-parser/model/device-spec/z3k-feature-flags';
import { select_object_array_parts } from './utils';
import { Z3kRoutes, Z3kSecondRoutes } from 'z3k-settings/routes/type';
import { removeNulls } from 'utils';
import { getWiredTemperatureSensor } from 'z3k-parser/lib/sensors/wired';
import { idExtractor } from 'z3k-parser/lib';
export const supportsAnyReactionOptions = (deviceSpec) => deviceSpec.has_notifications ||
    deviceSpec.io.show_output_actions ||
    deviceSpec.has_scenarios ||
    deviceSpec.show_commands_in_reactions;
export const reactionOptions = createSelector((config) => getDeviceSpec(config.device.name, config.device.hardware), select_object_array_parts('notifications', [
    'id',
    'name',
]), select_object_array_parts('output_actions', [
    'id',
    'name',
]), select_object_array_parts('commands', ['id', 'name', 'objectid', 'group']), select_object_array_parts('scenarios', ['id', 'name']), (device_spec, notifications, output_actions, commands, scenarios) => {
    return removeNulls([
        device_spec.has_notifications
            ? {
                name: 'Оповещения',
                url: Z3kRoutes.notifications,
                options: notifications.map((el) => {
                    return { ...el, url: Z3kSecondRoutes.notifications };
                }),
            }
            : null,
        device_spec.io.show_output_actions
            ? {
                name: 'Действия с выходом',
                url: Z3kRoutes.output_actions,
                options: output_actions.map((el) => {
                    return { ...el, url: Z3kSecondRoutes.output_actions };
                }),
            }
            : null,
        device_spec.has_scenarios
            ? {
                name: 'Сценарии',
                url: Z3kRoutes.scenarios,
                options: scenarios.map((el) => {
                    return { ...el, url: Z3kSecondRoutes.scenarios };
                }),
            }
            : null,
        device_spec.show_commands_in_reactions
            ? { name: 'Команды', url: null, options: commands }
            : null,
    ]);
});
export const complex_button_options = createSelector((config) => getDeviceSpec(config.device.name, config.device.hardware), select_object_array_parts('notifications', [
    'id',
    'name',
]), select_object_array_parts('output_actions', [
    'id',
    'name',
]), select_object_array_parts('commands', [
    'id',
    'name',
    'objectid',
]), idExtractor([HeatingMode]), (device_spec, notifications, output_actions, commands, heating_mode_ids) => {
    return removeNulls([
        device_spec.has_notifications
            ? { name: 'Оповещения', options: notifications }
            : null,
        device_spec.io.show_output_actions
            ? { name: 'Действия с выходом', options: output_actions }
            : null,
        device_spec.show_commands_in_reactions
            ? {
                name: 'Команды',
                options: commands.filter(({ objectid }) => !heating_mode_ids.includes(objectid)),
            }
            : null,
    ]);
});
export const reactionsAndGuardZonesOptions = createSelector(reactionOptions, select_object_array_parts('guard_zones', ['id', 'name']), (other, guard_zones) => [
    ...other,
    {
        name: 'Охранные зоны',
        url: Z3kRoutes.guard_zones,
        options: guard_zones.map((el) => {
            return { ...el, url: Z3kSecondRoutes.zone };
        }),
    },
]);
export const select_elements_options = createSelector((_, device_spec) => device_spec, HeatingCircuit.get.all, HeatingMode.get.all, getWiredTemperatureSensor, AnalogTemperatureSensor.get.all, AnalogInput.get.all, GuardZone.get.all, RadioSensor.get.all, RadioSensor433.get.all, WebElement.get.all, BoilerAdapter.get.all, (device_spec, hcs, hmodes, wts, ats, analog_inputs, guard_zones, rss, rss433, wes, ba) => removeNulls([
    device_spec.heating.has_heating
        ? {
            name: 'Отопительные контуры',
            url: Z3kRoutes.heating_circuits,
            options: hcs.map((el) => {
                return { ...el, url: Z3kSecondRoutes.heating_circuit };
            }),
        }
        : null,
    device_spec.heating.has_heating
        ? {
            name: 'Режимы отопления',
            url: Z3kRoutes.heating_modes,
            options: hmodes.map((el) => {
                return { ...el, url: Z3kSecondRoutes.heating_modes };
            }),
        }
        : null,
    {
        name: 'Цифровые термодатчики',
        url: Z3kRoutes.temperature_sensors,
        options: wts.map((el) => {
            return { ...el, url: Z3kSecondRoutes.wired_temperature_sensors };
        }),
    },
    {
        name: 'Аналоговые термодатчики',
        url: Z3kRoutes.temperature_sensors,
        options: ats.map((el) => {
            return { ...el, url: Z3kSecondRoutes.analog_temperature_sensors };
        }),
    },
    {
        name: 'Датчики',
        url: Z3kRoutes.sensors,
        options: analog_inputs.map((el) => {
            return { ...el, url: Z3kSecondRoutes.analog_inputs };
        }),
    },
    device_spec.has_guard_zones
        ? {
            name: 'Охранные зоны',
            url: Z3kRoutes.guard_zones,
            options: guard_zones.map((el) => {
                return { ...el, url: Z3kSecondRoutes.zone };
            }),
        }
        : null,
    device_spec.io.has_radiosensors
        ? {
            name: 'Радиодатчики',
            url: Z3kRoutes.radiosensors,
            options: rss.map((el) => {
                return { ...el, url: Z3kSecondRoutes.radiosensor868 };
            }),
        }
        : null,
    device_spec.io.has_radiosensors_433
        ? {
            name: 'Радиодатчики 433MHz',
            url: Z3kRoutes.radiosensors,
            options: rss433.map((el) => {
                return { ...el, url: Z3kSecondRoutes.radiosensor433 };
            }),
        }
        : null,
    device_spec.io.has_web_elements
        ? {
            name: 'Элементы управления',
            url: Z3kRoutes.web_elements,
            options: wes.map((el) => {
                return { ...el, url: Z3kSecondRoutes.web_elements };
            }),
        }
        : null,
    {
        name: 'Адаптеры котлов',
        url: Z3kRoutes.managed_mechanisms,
        options: ba.map((el) => {
            return { ...el, url: Z3kSecondRoutes.boiler_adapters };
        }),
    },
]));
export const select_user_and_roles_options = createSelector((_, device_spec) => device_spec, select_object_array_parts('users', ['id', 'name']), select_object_array_parts('user_roles', ['id', 'name']), (device_spec, users, user_roles) => removeNulls([
    {
        name: 'Пользователи',
        url: Z3kRoutes.users,
        options: users.map((el) => {
            return { ...el, url: Z3kSecondRoutes.users };
        }),
    },
    device_spec.has_user_roles
        ? {
            name: 'Роли',
            url: Z3kRoutes.users,
            options: user_roles.map((el) => {
                return { ...el, url: Z3kSecondRoutes.user_roles };
            }),
        }
        : null,
]));
export const inputsRadiosensorsOptionsSelector = createSelector(select_object_array_parts('analog_inputs', [
    'id',
    'name',
]), select_object_array_parts('radiosensors', [
    'id',
    'name',
    'type',
]), select_object_array_parts('radiosensors433', [
    'id',
    'name',
]), (analog_inputs, radiosensors, radiosensors433) => [
    {
        name: 'Датчики',
        url: Z3kRoutes.sensors,
        options: analog_inputs.map((el) => {
            return { ...el, url: Z3kSecondRoutes.analog_inputs };
        }),
    },
    {
        name: 'Радиодатчики',
        url: Z3kRoutes.radiosensors,
        options: RadioSensor.filterDisplayable(radiosensors).map((el) => {
            return { ...el, url: Z3kSecondRoutes.radiosensor868 };
        }),
    },
    {
        name: 'Радиодатчики 433МГц',
        url: Z3kRoutes.radiosensors,
        options: radiosensors433.map((el) => {
            return { ...el, url: Z3kSecondRoutes.radiosensor433 };
        }),
    },
]);
export const buzzersGuardIndicatorsOptionsSelector = createSelector(select_object_array_parts('buzzers', ['id', 'name']), select_object_array_parts('guard_indicators', [
    'id',
    'name',
]), (buzzers, guard_indicators) => [
    {
        name: 'Сирены',
        url: Z3kRoutes.buzzers,
        options: buzzers.map((el) => {
            return { ...el, url: Z3kSecondRoutes.buzzer };
        }),
    },
    {
        name: 'Индикаторы охраны',
        url: Z3kRoutes.guard_indicators,
        options: guard_indicators.map((el) => {
            return { ...el, url: Z3kSecondRoutes.indicator };
        }),
    },
]);
export const keysRadioTagsOptionsSelector = createSelector(select_object_array_parts('tm_keys', ['id', 'name']), (keys) => [
    {
        name: 'Ключи TM',
        url: Z3kRoutes.guard_zones,
        options: keys.map((el) => {
            return { ...el, url: Z3kSecondRoutes.tm_keys };
        }),
    },
]);
export const guardZonesOptionsSelector = createSelector(select_object_array_parts('guard_zones', ['id', 'name']), (guard_zones) => [
    {
        name: 'Охранные зоны',
        url: Z3kRoutes.guard_zones,
        options: guard_zones.map((el) => {
            return { ...el, url: Z3kSecondRoutes.zone };
        }),
    },
]);
export const usersOptionsSelector = createSelector(select_object_array_parts('users', ['id', 'name']), (users) => [
    {
        name: 'Пользователи',
        url: Z3kRoutes.users,
        options: users.map((el) => {
            return { ...el, url: Z3kSecondRoutes.user_roles };
        }),
    },
]);
export const boilersOptionsSelector = createSelector(select_object_array_parts('heating_circuits', ['id', 'name', 'type']), (heating_circuits) => [
    {
        name: 'Отопительные контуры',
        url: Z3kRoutes.heating_circuits,
        options: heating_circuits.filter(HeatingCircuit.isBoiler).map((el) => {
            return { ...el, url: Z3kSecondRoutes.heating_circuit };
        }),
    },
]);
export const selectBoilerAdaptersOptions = createSelector(BoilerAdapter.get.all, (adapters) => ({
    name: 'Адаптеры котлов',
    url: Z3kRoutes.managed_mechanisms,
    options: adapters.map((el) => {
        return { ...el, url: Z3kSecondRoutes.boiler_adapters };
    }),
}));
export const selectRelayControlsOptions = createSelector(RelayControl.get.all, (relays) => ({
    name: 'Релейные управления',
    url: Z3kRoutes.managed_mechanisms,
    options: relays.map((el) => {
        return { ...el, url: Z3kSecondRoutes.relay_controls };
    }),
}));
export const selectAnalogOutputOptions = createSelector(AnalogOutput.get.all, (sensor) => ({
    name: 'Аналоговые выходы',
    url: Z3kRoutes.managed_mechanisms,
    options: sensor.map((el) => {
        return { ...el, url: Z3kSecondRoutes.analog_outputs };
    }),
}));
export const selectPumpsOptions = createSelector(Pump.get.all, (pumps) => ({
    name: 'Насосы',
    url: Z3kRoutes.managed_mechanisms,
    options: pumps.map((el) => {
        return { ...el, url: Z3kSecondRoutes.pumps };
    }),
}));
export const selectThreewayTapsOptions = createSelector(ThreewayTap.get.all, (taps) => ({
    name: 'Краны смесителей',
    url: Z3kRoutes.managed_mechanisms,
    options: taps.map((el) => {
        return { ...el, url: Z3kSecondRoutes.threeway_taps };
    }),
}));
export const select_group_options = createSelector((_, device_spec) => device_spec, HeatingCircuit.get.all, HeatingMode.get.all, getWiredTemperatureSensor, AnalogTemperatureSensor.get.all, AnalogInput.get.all, GuardZone.get.all, RadioSensor.get.all, RadioSensor433.get.all, WebElement.get.all, BoilerAdapter.get.all, RelayControl.get.all, Pump.get.all, ThreewayTap.get.all, AnalogOutput.get.all, (device_spec, hcs, hmodes, wts, ats, analog_inputs, guard_zones, rss, rss433, wes, ba, relays, pumps, taps, analog_output) => removeNulls([
    device_spec.heating.has_heating && hcs.map.length > 0
        ? { name: 'Отопительные контуры', id: Z3KObjectId.HeatingCircuit }
        : null,
    device_spec.heating.has_heating && hmodes.map.length > 0
        ? { name: 'Режимы отопления', id: Z3KObjectId.HeatingMode }
        : null,
    wts.map.length > 0
        ? {
            name: 'Цифровые термодатчики',
            id: Z3KObjectId.WiredTemperatureSensor,
        }
        : null,
    ats.map.length > 0
        ? {
            name: 'Аналоговые термодатчики',
            id: Z3KObjectId.AnalogTemperatureSensor,
        }
        : null,
    analog_inputs.map.length > 0
        ? { name: 'Датчики', id: Z3KObjectId.AnalogInput }
        : null,
    device_spec.has_guard_zones && guard_zones.map.length > 0
        ? { name: 'Охранные зоны', id: Z3KObjectId.GuardZone }
        : null,
    device_spec.io.has_radiosensors && rss.map.length > 0
        ? { name: 'Радиодатчики', id: Z3KObjectId.RadioSensor }
        : null,
    device_spec.io.has_radiosensors_433 && rss433.map.length > 0
        ? { name: 'Радиодатчики 433MHz', id: Z3KObjectId.RadioSensor433 }
        : null,
    device_spec.io.has_web_elements && wes.map.length > 0
        ? { name: 'Элементы управления', id: Z3KObjectId.WebElement }
        : null,
    ba.map.length > 0
        ? { name: 'Адаптеры котлов', id: Z3KObjectId.BoilerAdapter }
        : null,
    relays.map.length > 0
        ? { name: 'Релейные управления', id: Z3KObjectId.RelayControl }
        : null,
    pumps.map.length > 0 ? { name: 'Насосы', id: Z3KObjectId.Pump } : null,
    taps.map.length > 0
        ? { name: 'Краны смесителей', id: Z3KObjectId.ThreewayTap }
        : null,
    analog_output.map.length > 0
        ? { name: 'Аналоговые выходы', id: Z3KObjectId.AnalogOutput }
        : null,
]));
